import { supabase } from './supabaseClient';
import { AuthErrorInterceptor } from './auth-error-interceptor';
import { FunctionsFetchError } from '@supabase/supabase-js';
import { authLogger } from './logging';
import { clearAllTranslationCaches } from './translation-loader';

// Timeout warning throttling to prevent console spam
let lastTimeoutWarning = 0;
const TIMEOUT_WARNING_THROTTLE_MS = 60000; // Only log timeout warnings once per minute

/**
 * Timeout Protection Utility
 * Wraps promises with timeout to prevent infinite hangs (e.g., IndexedDB issues)
 * Returns null if promise doesn't resolve within timeout
 */
function withTimeout<T>(promise: Promise<T>, timeoutMs: number, operation = 'operation'): Promise<T | null> {
  return Promise.race([
    promise,
    new Promise<null>((resolve) => {
      setTimeout(() => {
        // Only log timeout warning if 60+ seconds since last warning (prevent console spam)
        const now = Date.now();
        if (now - lastTimeoutWarning > TIMEOUT_WARNING_THROTTLE_MS) {
          authLogger.warn(`⏱️ Timeout after ${timeoutMs}ms for ${operation}`);
          lastTimeoutWarning = now;
        }
        resolve(null);
      }, timeoutMs);
    })
  ]);
}

// Helper functions for JWT debugging
const formatJWTForLogging = (jwt: string | undefined): string => {
  if (!jwt) return 'NO_JWT';
  return `${jwt.substring(0, 20)}...${jwt.substring(jwt.length - 8)}`;
};

// NOTE: getCurrentSessionInfo and getJWTInfo were removed because they called
// getSession() which serializes through navigator.locks in supabase-js v2.49+.
// Having them in the critical path caused cascading lock contention that
// prevented content from loading after login.

// Type definitions for better type safety
interface EdgeFunctionOptions {
  body?: Record<string, unknown> | unknown[];
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
}

interface EdgeFunctionResponse<T> {
  data: T | null;
  error: Error | null;
}

type AuthErrorHandler = (_error: Error) => void;

// Session refresh mutex to prevent race conditions
let refreshPromise: Promise<boolean> | null = null;
let globalRefreshPromise: Promise<boolean> | null = null;

/**
 * Get global refresh promise for coordination across components
 */
export function getGlobalRefreshPromise(): Promise<boolean> | null {
  return globalRefreshPromise;
}

/**
 * Set global refresh promise for coordination across components
 */
export function setGlobalRefreshPromise(promise: Promise<boolean> | null): void {
  globalRefreshPromise = promise;
}
let lastValidationTime = 0;
const VALIDATION_CACHE_MS = 10000; // 10 second cache for validation results - safer window to prevent masking JWT expiry

/**
 * Mark the session validation cache as fresh.
 * Call this after signInWithPassword / SIGNED_IN to prevent post-login
 * getSession() calls from queuing behind navigator.locks and timing out.
 */
export function markSessionFresh(): void {
  lastValidationTime = Date.now();
}

// Network failure recovery
let networkFailureDetected = false;

/**
 * Setup network reconnection recovery
 * Automatically retries failed operations when network comes back online
 */
function setupNetworkRecovery(): void {
  // Listen for browser's native network reconnection event
  window.addEventListener('online', async () => {
    if (networkFailureDetected) {
      authLogger.warn('🌐 Network reconnected - attempting recovery');
      networkFailureDetected = false;

      // Force session refresh to clear any stale state
      const refreshed = await ensureValidSession(true);

      if (refreshed) {
        authLogger.info('✅ Network recovery successful - session refreshed');
      } else {
        authLogger.warn('⚠️ Network reconnected but session refresh failed');
      }
    }
  });
}

// Initialize network recovery on module load
setupNetworkRecovery();

/**
 * Safely sign out the user, handling AuthSessionMissingError gracefully.
 * CRITICAL: Clears localStorage first (no lock), then fires server-side revocation
 * as fire-and-forget. supabase.auth.signOut() acquires the navigator lock and makes
 * a network call — if that call hangs, it blocks ALL getSession() calls in the app.
 * @returns Promise<boolean> - true if successful, false if there was an error
 */
export async function safeSignOut(): Promise<boolean> {
  try {
    // Clear translation caches first (safety net)
    clearAllTranslationCaches();
    authLogger.debug('Translation caches cleared during logout');

    // Step 1: Clear localStorage directly — no navigator lock, no network call
    const supabaseUrl = import.meta.env['VITE_PUBLIC_SUPABASE_URL'] as string | undefined;
    const projectRef = supabaseUrl?.split('//')[1]?.split('.')[0];
    if (projectRef) {
      const storageKey = `sb-${projectRef}-auth-token`;
      localStorage.removeItem(storageKey);
      localStorage.removeItem(`${storageKey}-code-verifier`);
    }

    // Step 2: Fire-and-forget server-side revocation
    // signOut() acquires the lock + makes API call, but we don't await it
    supabase.auth.signOut().catch((_error) => {
      authLogger.warn('Server-side signOut failed (non-critical)', _error);
    });

    authLogger.debug('User signed out (local state cleared, server revocation in background)');
    return true;
  } catch (_error) {
    authLogger.error('Sign out failed', _error);
    clearAllTranslationCaches();
    return false;
  }
}

/**
 * Check if the current user has an active session
 * @returns Promise<boolean> - true if there's an active session
 */
export async function hasActiveSession(): Promise<boolean> {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
      authLogger.error('Auth operation failed', error);
      return false;
    }

    return !!session;
  } catch (_error) {
    authLogger.error('Auth operation failed', _error);
    return false;
  }
}

/**
 * Refresh the current session to get updated JWT claims
 * @returns Promise<boolean> - true if refresh was successful
 */
export async function refreshSession(): Promise<boolean> {
  try {
    authLogger.debug('🔄 [REFRESH] Starting session refresh');

    const { data, error } = await supabase.auth.refreshSession();

    if (error) {
      authLogger.error('🔄 [REFRESH] Refresh failed', { error: error.message });
      return false;
    }

    if (!data.session) {
      authLogger.warn('🔄 [REFRESH] No session returned after refresh');
      return false;
    }

    // Validate the refreshed JWT directly from the returned data
    // (NO extra getSession() call — avoids navigator lock contention)
    const { access_token } = data.session;
    if (access_token) {
      try {
        const payload = JSON.parse(atob(access_token.split('.')[1] || ''));
        if (!payload.sub) {
          authLogger.error('🔄 [REFRESH] Refreshed token missing sub claim');
          return false;
        }

        authLogger.debug('🔄 [REFRESH] Session refresh successful', {
          jwt: formatJWTForLogging(access_token),
          expiry: payload.exp ? new Date(payload.exp * 1000).toISOString() : 'N/A'
        });
      } catch (jwtError) {
        authLogger.error('🔄 [REFRESH] Error parsing JWT after refresh', { jwtError });
        return false;
      }
    }

    return true;
  } catch (_error) {
    authLogger.error('🔄 [REFRESH] Refresh threw exception', { error: _error });
    return false;
  }
}

// NOTE: validateSessionJWT() was removed — its logic is now inlined in ensureValidSession()
// to avoid an extra getSession() call through navigator.locks.

/**
 * Ensure we have a valid session before making edge function calls
 * Automatically refreshes if needed with mutex to prevent race conditions
 * Enhanced to coordinate with global proactive refresh
 * @param forceRefresh - If true, bypass validation cache and force fresh validation
 * @returns Promise<boolean> - true if session is ready
 */
export async function ensureValidSession(forceRefresh = false): Promise<boolean> {
  try {
    // 1. Check validation cache — NO getSession() call needed
    if (!forceRefresh && Date.now() - lastValidationTime < VALIDATION_CACHE_MS) {
      return true;
    }

    // 2. Wait for any in-progress refresh (avoids duplicate refreshes)
    if (globalRefreshPromise) {
      try {
        const result = await globalRefreshPromise;
        if (result) lastValidationTime = Date.now();
        return result;
      } catch {
        return false;
      }
    }

    if (refreshPromise) {
      try {
        const result = await refreshPromise;
        return result;
      } catch {
        return false;
      }
    }

    // 3. SINGLE getSession() call with generous timeout
    // supabase-js v2.49+ serializes ALL getSession() through navigator.locks.
    // Using one call with a long timeout prevents the cascade that was killing the app.
    const sessionResult = await withTimeout(
      supabase.auth.getSession(),
      10000,
      'ensureValidSession: getSession()'
    );

    if (!sessionResult) {
      // Timeout = navigator lock is busy (another refresh likely in progress).
      // Do NOT start a new refresh — that would add MORE lock-queued calls.
      authLogger.warn('ensureValidSession: getSession() timed out — backing off');
      return false;
    }

    const { data: { session } } = sessionResult;
    if (!session?.access_token) {
      return false;
    }

    // 4. Validate JWT inline from the single result (NO extra getSession call)
    const payload = JSON.parse(atob(session.access_token.split('.')[1] || '')) as {
      sub?: string;
      exp?: number;
    };

    if (!payload.sub) {
      authLogger.error('JWT missing sub claim');
      return false;
    }

    const now = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = payload.exp ? payload.exp - now : 0;

    // 5. Token expired or expiring within 30s, or force refresh requested → refresh
    if (forceRefresh || timeUntilExpiry <= 30) {
      authLogger.warn('Session refresh needed', { forceRefresh, timeUntilExpiry });

      const refreshOperation = performSessionRefresh();
      refreshPromise = refreshOperation;
      globalRefreshPromise = refreshOperation;

      try {
        const result = await refreshOperation;
        if (result) lastValidationTime = Date.now();
        return result;
      } finally {
        refreshPromise = null;
        globalRefreshPromise = null;
      }
    }

    // 6. Valid — update cache
    lastValidationTime = Date.now();

    // 7. If expiring within 5 min but > 60s, trigger background refresh (fire-and-forget)
    if (timeUntilExpiry > 60 && timeUntilExpiry < 300) {
      performSessionRefresh().catch(() => {});
    }

    return true;
  } catch (_error) {
    authLogger.error('ensureValidSession failed', { error: _error });
    refreshPromise = null;
    globalRefreshPromise = null;
    return false;
  }
}

/**
 * Internal function to perform the actual session refresh
 * This is separated to be used with the mutex
 */
async function performSessionRefresh(): Promise<boolean> {
  try {
    // refreshSession() already validates the JWT from the returned data
    // (no extra getSession/validateSessionJWT call needed)
    const refreshed = await refreshSession();
    if (!refreshed) {
      authLogger.error('Failed to refresh session');
      return false;
    }

    authLogger.debug('Session successfully refreshed');
    return true;
  } catch (_error) {
    authLogger.error('Session refresh failed', _error);
    return false;
  }
}

/**
 * Wrapper for edge function calls that handles auth errors gracefully
 * Automatically refreshes JWT and retries once if auth session is missing/invalid
 * Now integrates with the global auth error handler
 */
export async function callEdgeFunctionWithAuthRefresh<T>(
  functionName: string,
  options?: EdgeFunctionOptions,
  authErrorHandler?: AuthErrorHandler
): Promise<EdgeFunctionResponse<T>> {
  const maxRetries = 2; // Increased retries for better recovery
  let attempt = 0;
  const callId = Math.random().toString(36).substring(7);

  // STEP 1: Ensure valid session before any calls
  // (NO getCurrentSessionInfo calls — they caused navigator lock contention)
  const sessionValid = await ensureValidSession();
  if (!sessionValid) {
    authLogger.warn(`📞 [CALL-${callId}] Session validation failed for '${functionName}' — likely navigator lock timeout, NOT calling authErrorHandler`);
    // CRITICAL: Do NOT call authErrorHandler here.
    // A getSession() timeout (navigator lock contention) is transient — NOT a real auth error.
    // Calling authErrorHandler would trigger clearAuthState() → signOut() → kick user out.
    const sessionError = new Error('Session validation timed out - edge function call skipped');
    (sessionError as Error & { code: string }).code = 'SESSION_VALIDATION_TIMEOUT';
    return { data: null, error: sessionError };
  }
  
  while (attempt <= maxRetries) {
    try {
      // Only log attempt details for retries (not the first attempt)
      if (attempt > 0) {
        authLogger.debug(`📞 [CALL-${callId}] Retry attempt ${attempt + 1}/${maxRetries + 1} for '${functionName}'`, {
          attempt,
          timestamp: Date.now()
        });
      }

      const { data, error } = await supabase.functions.invoke(functionName, options);

      // Only log response details if there's an error or this is a retry
      if (error || attempt > 0) {
        authLogger.debug(`📞 [CALL-${callId}] Edge function response for '${functionName}'`, {
          hasData: !!data,
          hasError: !!error,
          errorType: error?.message?.substring(0, 50) || 'NO_ERROR',
          errorStatus: (error as { status?: number })?.status,
          attempt
        });
      }
      
      // Check for auth-related errors using the enhanced Supabase interceptor
      if (error) {
        // Special handling for "missing sub claim" errors
        if (error.message?.includes('missing sub claim') || error.details?.includes('missing sub claim')) {
          authLogger.error('Missing sub claim detected - JWT token corruption');
          
          // For missing sub claim, we should try to refresh and retry
          if (attempt < maxRetries) {
            authLogger.warn('Attempting session refresh for missing sub claim...');
            const refreshed = await ensureValidSession();
            if (refreshed) {
              attempt++;
              continue; // Retry the call
            }
          }
          
          // If refresh fails, notify handler
          if (authErrorHandler) {
            authErrorHandler({ ...error, code: 'JWT_SUB_CLAIM_MISSING' });
          }
          return { data: null, error: { ...error, code: 'JWT_SUB_CLAIM_MISSING' } };
        }

        // DEPLOYMENT AUTH ERROR DETECTION: JWT desynchronized after Edge Function deployment
        // These errors require a fresh JWT (session refresh), not just waiting
        const isDeploymentAuthError =
          error.code === 'AUTH_DEPLOYMENT_RACE_CONDITION' ||
          error.code === 'AUTH_TOKEN_INVALID' ||
          error.code === 'AUTH_TOKEN_EXPIRED' ||
          (error.status === 503 && error.message?.includes('Auth session missing'));

        if (isDeploymentAuthError) {
          if (attempt === 0) {
            authLogger.warn(`🔄 Deployment auth error detected for '${functionName}' - refreshing session to get new JWT`);
          }

          if (attempt < maxRetries) {
            // Force session refresh to get NEW JWT (bypass validation cache)
            const refreshed = await ensureValidSession(true);

            if (refreshed) {
              authLogger.info(`✅ Session refreshed for '${functionName}' - retrying with new JWT`);
              attempt++;
              continue; // Retry with NEW JWT
            } else {
              authLogger.error(`❌ Session refresh failed for '${functionName}' - user may need to re-authenticate`);
              if (authErrorHandler) {
                authErrorHandler({ ...error, code: 'AUTH_REFRESH_FAILED_AFTER_DEPLOYMENT' });
              }
              return { data: null, error: { ...error, code: 'AUTH_REFRESH_FAILED_AFTER_DEPLOYMENT' } };
            }
          } else {
            authLogger.error(`❌ Deployment auth error retries exhausted for '${functionName}'`);
            if (authErrorHandler) {
              authErrorHandler({ ...error, code: 'DEPLOYMENT_AUTH_RETRIES_EXHAUSTED', functionName });
            }
            return { data: null, error: { ...error, code: 'DEPLOYMENT_AUTH_RETRIES_EXHAUSTED', functionName } };
          }
        }

        // COLD START ERROR DETECTION: Edge Runtime warming up (JWT is fine, just wait)
        const isColdStartError = error.status === 503 ||
                               error.status === 409 ||
                               (error.status === 500 && error.message?.includes('Function invocation failed'));

        if (isColdStartError) {
          const coldStartType = error.status === 503 ? 'cold start (503)' :
                               error.status === 409 ? 'cold start conflict (409)' :
                               'deployment race condition';

          // Only log warning on first attempt to reduce console noise
          if (attempt === 0) {
            authLogger.warn(` ${coldStartType} detected for '${functionName}' - attempting silent retry`);
          }

          // Use exponential backoff for cold start errors
          if (attempt < maxRetries) {
            const baseDelay = attempt === 0 ? 500 : 200; // Longer initial delay for cold starts
            const delay = baseDelay * Math.pow(1.5, attempt); // 500ms, 300ms, 450ms

            // Don't log every retry to keep console clean
            if (attempt < 2) {
              authLogger.warn(` Cold start retry ${attempt + 2} for '${functionName}' after ${delay}ms`);
            }

            await new Promise(resolve => setTimeout(resolve, delay));

            // Don't refresh session for cold start errors - token is likely fine
            attempt++;
            continue; // Retry the call
          } else {
            // Only show error to user after all retries exhausted
            authLogger.error(` Cold start retries exhausted for '${functionName}' - this may indicate a real issue`);
            if (authErrorHandler) {
              authErrorHandler({ ...error, code: 'COLD_START_RETRIES_EXHAUSTED', functionName });
            }
            return { data: null, error: { ...error, code: 'COLD_START_RETRIES_EXHAUSTED', functionName } };
          }
        }

        // Special handling for FunctionsFetchError (Network-level failure)
        if (error instanceof FunctionsFetchError) {
          authLogger.warn(`🌐 Network error detected for '${functionName}' - marking for recovery`);

          // Mark network failure for recovery when network comes back
          networkFailureDetected = true;

          // Try session refresh for fetch errors (might help if it's auth-related)
          if (attempt < maxRetries) {
            authLogger.warn('Attempting session refresh for network error...');
            const refreshed = await ensureValidSession();
            if (refreshed) {
              attempt++;
              continue; // Retry the call
            }
          }

          // Notify handler with network-specific error code
          if (authErrorHandler) {
            const networkError = error as Error & { code?: string; shouldRedirectToLogin?: boolean };
            networkError.code = 'NETWORK_ERROR';
            networkError.shouldRedirectToLogin = false; // Don't redirect on network errors
            authErrorHandler(networkError);
          }

          const networkError: Error & { code?: string } = error as Error & { code?: string };
          networkError.code = 'NETWORK_ERROR';
          networkError.message = 'Connection lost. Waiting for network...';

          return {
            data: null,
            error: networkError
          };
        }
        
        const errorDetails = await AuthErrorInterceptor.analyzeSupabaseError(error);
        
        if (errorDetails) {
          authLogger.warn(` Auth error detected on attempt ${attempt + 1}:`, errorDetails.type);
          
          // For critical auth errors (like user_not_found), don't retry and notify handler
          if (errorDetails.type === 'user_not_found' || errorDetails.type === 'insufficient_permissions') {
            authLogger.error('Critical auth error, notifying handler and giving up');
            if (authErrorHandler) {
              authErrorHandler(error);
            }
            return { data: null, error };
          }
          
          // For potentially recoverable errors, try refresh + retry
          if (attempt < maxRetries && (errorDetails.type === 'session_expired' || errorDetails.type === 'invalid_token')) {
            authLogger.warn('Attempting to refresh session and retry...');

            const refreshed = await ensureValidSession(true);
            if (!refreshed) {
              authLogger.error('Failed to refresh session, notifying handler');
              if (authErrorHandler) {
                authErrorHandler({ ...error, code: 'AUTH_REFRESH_FAILED' });
              }
              return { data: null, error: { ...error, code: 'AUTH_REFRESH_FAILED' } };
            }
            
            attempt++;
            continue; // Retry the call
          }
        }
      }
      
      // Return the result (success or non-auth error)
      return { data, error };
      
    } catch (_error) {
      authLogger.error('Auth operation failed', _error);

      // Check if the caught exception is auth-related
      const errorDetails = AuthErrorInterceptor.analyzeError(_error);
      if (errorDetails && authErrorHandler) {
        authErrorHandler(_error as Error);
      }

      return { data: null, error: _error as Error };
    }
  }
  
  // This should never be reached, but just in case
  return { data: null, error: new Error('Maximum retries exceeded') };
}

